import {useState, useRef, useContext} from "react";
import Modal from "react-bootstrap/Modal";
import L from "leaflet";

import Loader from "components/loaders/Loader";
import AuthContext from "context/AuthContext";
import createWasteIconJS from "components/waste-icons/svgs/createWasteIconJS";
import getMarkerFontAwesomeJS from "components/waste-icons/svgs/getMarkerFontAwesomeJS";
import DateTimesUtils from "core/utils/DateTimesUtils";
import { CARTO_TILE_URL, CARTO_TILE_OPTIONS } from 'core/mapsConfig';

/*
 * version: 2.0.0
 * 18-02-2026
 * Preparada para pintar el geojson de Azure
 * */
const TabPreviewMapTravelLeaflet = ({show, setShow, assetsIn, assetsOut, travel, pointsModels}) => {
    const {panel} = useContext(AuthContext);
    const mapRef = useRef(null);
    const assetsLayerRef = useRef(L.layerGroup([]));
    const assetsInLayerRef = useRef(L.layerGroup([]));
    const assetsOutLayerRef = useRef(L.layerGroup([]));
    const routeLayerRef = useRef(L.featureGroup([]));

    const checkpoints = useRef([]);
    const [loading, setLoading] = useState(true);

    const handleEntered = () => {
        mapRef.current = L.map("route-dynamic-map").setView(panel.coords, 13);
        // Capa base
        L.tileLayer(CARTO_TILE_URL, CARTO_TILE_OPTIONS).addTo(mapRef.current);

        // Inicializar las capas
        routeLayerRef.current.addTo(mapRef.current);
        assetsInLayerRef.current.addTo(mapRef.current);
        //assetsOutLayerRef.current.addTo(mapRef.current);

        // Configurar control de capas
        const overlayMaps = {
            Ruta: routeLayerRef.current,
            Incluidos: assetsInLayerRef.current,
            Excluidos: assetsOutLayerRef.current,
        };

        // Añadir control de capas al mapa
        L.control.layers(null, overlayMaps).addTo(mapRef.current);

        addAssetsMap();
        paintRouteAll(travel.current);

        //paintRouteStep(travel.current);
        //playRouteSteps(travel.current);
        setLoading(false);
    };
    const handlePlay = async () => {
        await paintRouteStep(travel.current);
        paintRouteAll(travel.current);
    };

    const addAssetsMap = () => {
        // Limpiar todas las capas
        assetsInLayerRef.current.clearLayers();
        assetsOutLayerRef.current.clearLayers();
        routeLayerRef.current.clearLayers();

        // Procesar los activos incluidos
        checkpoints.current = assetsIn.map((way) => {
            return {
                type: way.asset.waste_type,
                filling: way.asset.filling,
                uid: way.asset.uid,
                lat: way.asset.lat,
                lng: way.asset.lng,
            };
        });

        // Añadir marcadores para assetsIn
        let icon, name;
        checkpoints.current.forEach((point, index) => {
            icon = createWasteIconJS(point.type, point.filling);
            name = "UID: " + point.uid;
            const marker = L.marker([point.lat, point.lng], {
                icon,
            }).bindPopup(name);
            assetsInLayerRef.current.addLayer(marker);
        });
        // Añadir marcadores para assetsOut
        if (assetsOut && assetsOut.length > 0) {
            assetsOut.forEach((asset) => {
                if (asset.asset) {
                    icon = createWasteIconJS(asset.asset.waste_type, asset.asset.filling);
                    name = "UID: " + asset.asset.uid + " (Excluido)";
                    const marker = L.marker([asset.asset.lat, asset.asset.lng], {
                        icon,
                        opacity: 0.8, // Opacidad reducida para diferenciarlos
                    }).bindPopup(name);
                    assetsOutLayerRef.current.addLayer(marker);
                }
            });
        }

        // add waypoints by pointsModels.current
        const POINTS = ["refueling", "unload", "origin", "destination"];
        const waypoints = [];
        Object.keys(pointsModels.current).forEach((key) => {
            if (POINTS.includes(key) && pointsModels.current[key] && !waypoints.includes(pointsModels.current[key].id)) {
                icon = getMarkerFontAwesomeJS(pointsModels.current[key].icon);
                name = pointsModels.current[key].name;
                const marker = L.marker([pointsModels.current[key].lat, pointsModels.current[key].lng], {
                    icon,
                })
                    .addTo(mapRef.current)
                    .bindPopup(name);
                assetsLayerRef.current.addLayer(marker);
                waypoints.push(pointsModels.current[key].id);
            }
        });
    };
    const paintRouteAll = (_travel) => {
        routeLayerRef.current.clearLayers();

        // El último feature contiene el MultiLineString con la ruta completa
        const coordinates = _travel.geometry.coordinates;
        // MultiLineString tiene arrays de arrays, así que concatenamos todo
        const allCoords = coordinates.flat();

        const latlngs = allCoords.map((coord) => [coord[1], coord[0]]);
        const polyline = L.polyline(latlngs, {color: "blue", opacity: 0.8, weight: 2}).addTo(mapRef.current);
        routeLayerRef.current.addLayer(polyline);

        mapRef.current.fitBounds(polyline.getBounds());
    };
    const paintRouteStep = async (_travel) => {
        if (!routeLayerRef.current || !_travel?.geometry?.coordinates) return;

        routeLayerRef.current.clearLayers();

        // Usaremos un array para ir acumulando los puntos y que la línea crezca
        const accumulatedCoords = [];

        for (const step of _travel.geometry.coordinates) {
            // Convertimos el step de [lng, lat] a [lat, lng] para Leaflet
            const stepCoords = step.map(([lng, lat]) => [lat, lng]);

            for (const coord of stepCoords) {
                accumulatedCoords.push(coord);

                // 1. Dibujamos la línea acumulada
                if (accumulatedCoords.length >= 2) {
                    // Para que sea eficiente, borramos la polilínea anterior antes de poner la nueva
                    // Opcional: si quieres dejar el rastro, no borres, pero saturará el DOM
                    routeLayerRef.current.eachLayer((l) => {
                        if (l instanceof L.Polyline && !(l instanceof L.Circle)) {
                            routeLayerRef.current.removeLayer(l);
                        }
                    });

                    const polyline = L.polyline(accumulatedCoords, {
                        color: "green",
                        opacity: 0.8,
                        weight: 2,
                    });
                    routeLayerRef.current.addLayer(polyline);
                }

                // Pequeña pausa entre coordenadas para que se vea fluida
                await DateTimesUtils.sleep(50);
            }

            // 2. Al finalizar el step, añadimos el círculo (el hito)
            const lastLatLng = stepCoords.at(-1);
            if (lastLatLng) {
                const circle = L.circle(lastLatLng, {
                    radius: 15, // Ajusta según el zoom
                    color: "violet",
                    fillColor: "violet",
                    fillOpacity: 0.5,
                    weight: 2,
                });
                routeLayerRef.current.addLayer(circle);
            }

            // Pausa extra opcional al final de cada tramo (step)
            await DateTimesUtils.sleep(200);
        }

        // Ajustar cámara al final si es necesario
        const bounds = routeLayerRef.current.getBounds();
        if (bounds.isValid()) mapRef.current.fitBounds(bounds, {padding: [50, 50]});
    };
    // <button type="button" className="btn btn-sm btn-outline-secondary ms-3" onClick={handlePlay}>
    //     <FontAwesomeIcon icon="play" /> Ruta
    // </button>;
    return (
        <Modal show={show} fullscreen={true} onHide={() => setShow(false)} onEntered={handleEntered}>
            <Modal.Header closeButton>
                <Modal.Title>Mapa ruta dinamica</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div>
                    <div id="route-dynamic-map" style={{height: "calc(100vh - 90px)", width: "100%"}}>
                        <Loader show={loading} />
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};
export default TabPreviewMapTravelLeaflet;
